import { KonvaEventObject } from "konva/lib/Node";
import { useDeferredValue, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Group } from "react-konva";
import {
  Dimensions,
  selectCanvasDimensions,
  setSelectedCameraSkuSection,
  updateCanvasDimensions,
} from "@/entities/planogram";
import { SkuSection, WithIndex } from "@/shared/api";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { Canvas2DWrapper } from "./canvas-2d-wrapper";
import { createDragBoundHandler } from "./helpers/createDragBoundHandler";
import { Polygon } from "./polygon";
import { PolygonVisibilityWrapper } from "./polygon-visibility-wrapper";

export function PlanogramCanvas({
  renderVideo,
  cameraSkuSections,
}: {
  renderVideo: React.ReactNode;
  cameraSkuSections: WithIndex<SkuSection>[] | null;
}) {
  const [isPanning, setIsPanning] = useState(false);

  const dispatch = useAppDispatch();
  const canvasDimensions = useAppSelector(selectCanvasDimensions);

  const deferredCanvasDimensions = useDeferredValue(canvasDimensions);

  const onCanvasResize = (newSize: Dimensions) =>
    dispatch(updateCanvasDimensions(newSize));

  const checkDeselect = (event: KonvaEventObject<PointerEvent>) => {
    const clickedOnEmpty = event.target === event.target!.getStage();

    if (clickedOnEmpty) {
      dispatch(setSelectedCameraSkuSection(null));
    }
  };

  const onPointerDown = (event: KonvaEventObject<PointerEvent>) => {
    if (isPanning) {
      document.body.style.cursor = "grabbing";
      return;
    }

    checkDeselect(event);
  };

  const onPointerUp = () => {
    if (isPanning) {
      document.body.style.cursor = "grab";
    }
  };

  const onSpaceDown = () => {
    if (!isPanning) {
      setIsPanning(true);
      document.body.style.cursor = "grab";
    }
  };

  const onSpaceUp = () => {
    document.body.style.cursor = "default";
    setIsPanning(false);
  };

  useHotkeys("space", onSpaceDown, { preventDefault: true, keydown: true });
  useHotkeys("space", onSpaceUp, { preventDefault: true, keyup: true });

  const onWheel = (e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();

    if (!e.evt.ctrlKey) return;

    const isTouchPad =
      "wheelDeltaY" in e.evt
        ? Math.abs(e.evt.wheelDeltaY as number) === 120
        : e.evt.deltaMode === 0;

    const scaleBy = isTouchPad ? 1.055 : 1.15;
    const stage = e.target.getStage();

    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    // how to scale? Zoom in? Or zoom out?
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    newScale = Math.max(1, Math.min(10, newScale));

    stage.scale({ x: newScale, y: newScale });

    const newPos = {
      x: -(mousePointTo.x - pointer.x / newScale) * newScale,
      y: -(mousePointTo.y - pointer.y / newScale) * newScale,
    };

    newPos.x = Math.min(newPos.x, 0);
    newPos.x = Math.max(newPos.x, canvasDimensions.width * (1 - newScale));
    newPos.y = Math.min(newPos.y, 0);
    newPos.y = Math.max(newPos.y, canvasDimensions.height * (1 - newScale));

    stage.position(newPos);
  };

  return (
    <Canvas2DWrapper
      className="grow"
      size={deferredCanvasDimensions}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onResize={onCanvasResize}
      onWheel={onWheel}
      dragBoundFunc={createDragBoundHandler(deferredCanvasDimensions)}
      draggable={isPanning}
      renderUnderlay={renderVideo}
    >
      <Group name="camera-sku-sections">
        <PolygonVisibilityWrapper>
          {cameraSkuSections?.map((cameraSkuSection, index) => (
            <Polygon
              key={cameraSkuSection.id}
              index={index}
              cameraSkuSection={cameraSkuSection}
              canvasDimensions={deferredCanvasDimensions}
              onSelect={() => {
                dispatch(setSelectedCameraSkuSection(cameraSkuSection));
              }}
            />
          ))}
        </PolygonVisibilityWrapper>
      </Group>
    </Canvas2DWrapper>
  );
}
